<script lang="ts">
	import { Button, Container, Radio, Text, toast } from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { format } from 'svelte-i18n';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig, VentoraInMode } from 'src/types/ventora_fan';
	import {
		VENTORA_IO_HEADING,
		VENTORA_IO_SUBTITLE,
		VENTORA_IO_SECTION_INITIAL,
		VENTORA_IO_SECTION_INPUT_TYPE,
		VENTORA_IO_SECTION_INPUT_MODE,
		VENTORA_IO_INIT_DESC,
		VENTORA_IO_INIT_PILL,
		VENTORA_IO_INPUT_TYPE_DESC,
		VENTORA_IO_IN_MODE_DESC,
		VENTORA_IO_IN_MODE_PILL_LABEL,
	} from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		patchVentoraFanConfig,
		patchVentoraInputType,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraInModeOptions,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let test_setting: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);

	const initialKeys = ['off', 'on', 'restore_last'] as const;
	type InitialKey = (typeof initialKeys)[number];

	const inputKeys = ['switch', 'button'] as const;
	type InputTypeKey = (typeof inputKeys)[number];

	const initialLast = initialKeys.length - 1;
	const inputLast = inputKeys.length - 1;
	const modeLast = ventoraInModeOptions.length - 1;

	const isInitialKey = (v: string | undefined): v is InitialKey =>
		initialKeys.includes(v as InitialKey);

	let loading = false;
	let dirty = false;
	let draftInMode: VentoraInMode = defaultVentoraFanConfig.in_mode;
	let draftInitial: InitialKey = 'restore_last';
	let draftType: InputTypeKey = 'switch';

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);
	$: inputEntry = $settingsStore?.[`input:${device.channel}`] as { type?: string } | undefined;

	$: if (!dirty) {
		draftInMode = config.in_mode;
		const rawInit = config.initial_state;
		draftInitial = isInitialKey(rawInit) ? rawInit : 'restore_last';
		draftType = inputEntry?.type === 'button' ? 'button' : 'switch';
	}

	$: savedInputType = inputEntry?.type === 'button' ? 'button' : 'switch';
	$: savedInitial = isInitialKey(config.initial_state) ? config.initial_state : 'restore_last';

	$: controlDisabled = loading || isOffline;

	const pickInMode = (mode: VentoraInMode) => {
		draftInMode = mode;
		dirty = true;
	};

	const pickInitial = (k: InitialKey) => {
		draftInitial = k;
		dirty = true;
	};

	const pickType = (t: InputTypeKey) => {
		draftType = t;
		dirty = true;
	};

	const save = async () => {
		if (!dirty || isOffline) return;
		loading = true;
		try {
			if (draftType !== savedInputType) {
				const okType = await patchVentoraInputType(device, draftType);
				if (!okType) {
					toast.open({
						type: 'danger',
						title: $format('btn_type_err_toast', {
							default: 'Error! Could not change button type.',
						}),
					});
					return;
				}
			}

			const needFan = draftInMode !== config.in_mode || draftInitial !== savedInitial;

			if (needFan) {
				const ok = await patchVentoraFanConfig(device, {
					in_mode: draftInMode,
					initial_state: draftInitial,
				});
				if (!ok) {
					toast.open({
						type: 'danger',
						title: $format('ventora_io_save_failed', {
							default: 'Could not apply fan input settings.',
						}),
					});
					return;
				}
			}

			dirty = false;
			toast.open({
				type: 'success',
				title: $format('ventora_io_save_ok', {
					default: 'Input settings saved.',
				}),
			});
		} finally {
			loading = false;
		}
	};
</script>

<div class="mb-l">
	<Text class="text-bold" tag="h2" styleType="body">
		{$format(VENTORA_IO_HEADING.key, { default: VENTORA_IO_HEADING.default })}
	</Text>
	<Text class="mt-xxs text-color-muted" styleType="body-small">
		{$format(VENTORA_IO_SUBTITLE.key, { default: VENTORA_IO_SUBTITLE.default })}
	</Text>
</div>

<Container type="base" class="mb-l" padding="xs" aria-labelledby="ventora-io-initial-heading">
	<Text id="ventora-io-initial-heading" class="text-bold mb-s" styleType="body-small" tag="h3">
		{$format(VENTORA_IO_SECTION_INITIAL.key, { default: VENTORA_IO_SECTION_INITIAL.default })}
	</Text>
	{#each initialKeys as key, i}
		<Radio
			size="m"
			selected={draftInitial}
			value={key}
			class={classMap({ 'mb-s': i < initialLast })}
			disabled={controlDisabled}
			on:click={() => pickInitial(key)}
			id={`ventora-io-init-${key}`}
			info={$showNewLayout
				? $format(VENTORA_IO_INIT_DESC[key].key, {
						default: VENTORA_IO_INIT_DESC[key].default,
					})
				: undefined}
		>
			{$format(VENTORA_IO_INIT_PILL[key].key, {
				default: VENTORA_IO_INIT_PILL[key].default,
			})}
		</Radio>
	{/each}
</Container>

<Container type="base" class="mb-l" padding="xs" aria-labelledby="ventora-io-type-heading">
	<Text id="ventora-io-type-heading" class="text-bold mb-s" styleType="body-small" tag="h3">
		{$format(VENTORA_IO_SECTION_INPUT_TYPE.key, { default: VENTORA_IO_SECTION_INPUT_TYPE.default })}
	</Text>
	{#each inputKeys as t, i}
		<Radio
			size="m"
			selected={draftType}
			value={t}
			class={classMap({ 'mb-s': i < inputLast })}
			disabled={controlDisabled}
			on:click={() => pickType(t)}
			id={`ventora-io-type-${t}`}
			info={$showNewLayout
				? $format(VENTORA_IO_INPUT_TYPE_DESC[t].key, {
						default: VENTORA_IO_INPUT_TYPE_DESC[t].default,
					})
				: undefined}
		>
			{t === 'switch'
				? $format('working_type_switch', { default: 'Switch' })
				: $format('working_type_button', { default: 'Button' })}
		</Radio>
	{/each}
</Container>

<Container type="base" class="mb-s" padding="xs" aria-labelledby="ventora-io-mode-heading">
	<Text id="ventora-io-mode-heading" class="text-bold mb-s" styleType="body-small" tag="h3">
		{$format(VENTORA_IO_SECTION_INPUT_MODE.key, { default: VENTORA_IO_SECTION_INPUT_MODE.default })}
	</Text>
	{#each ventoraInModeOptions as mode, i}
		<Radio
			size="m"
			selected={draftInMode}
			value={mode}
			class={classMap({ 'mb-s': i < modeLast })}
			disabled={controlDisabled}
			on:click={() => pickInMode(mode)}
			id={`ventora-io-mode-${mode}`}
			info={$showNewLayout
				? $format(VENTORA_IO_IN_MODE_DESC[mode].key, {
						default: VENTORA_IO_IN_MODE_DESC[mode].default,
					})
				: undefined}
		>
			{$format(VENTORA_IO_IN_MODE_PILL_LABEL[mode].key, {
				default: VENTORA_IO_IN_MODE_PILL_LABEL[mode].default,
			})}
		</Radio>
	{/each}
</Container>

<Button
	class="mt-s"
	{loading}
	size="m"
	width="full"
	variant="primary"
	disabled={!dirty || loading || isOffline}
	on:click={save}
	id="button-ventora-io-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
